import { AfterViewInit, Component, computed, effect, ElementRef, HostListener, inject, signal, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { AuthService } from '@features/auth/auth.service';
import {
  NETWORK_METHODS,
  NETWORK_STATES,
  NetworkMethod,
  NetworkMonitorService,
} from './network-monitor.service';
import { MockRule, NetworkEntry, NetworkEntryState, SavedRequest } from './network.types';

interface FolderGroup<T = SavedRequest> {
  folder: string; // '' for ungrouped
  items: T[];
}
import { NetworkRowComponent } from './network-row.component';
import { NetworkSavedRowComponent } from './network-saved-row.component';
import { NetworkMockRowComponent } from './network-mock-row.component';
import { SaveFavoriteDialog } from './network-dialogs.component';
import { DevBottomBarService } from '../dev-bottom-bar.service';

type Tab = 'monitor' | 'saved' | 'mocks';

/** UI label for each response state chip. */
const STATE_LABELS: Record<NetworkEntryState, string> = {
  success: 'OK',
  error: 'ERROR',
  pending: 'PENDING',
  cancelled: 'CANCELLED',
};

@Component({
  selector: 'app-dev-network-panel',
  standalone: true,
  imports: [MatIconModule, NetworkRowComponent, NetworkSavedRowComponent, NetworkMockRowComponent],
  templateUrl: './network-panel.component.html',
  styleUrls: ['./network-panel.component.css'],
})
export class NetworkPanelComponent implements AfterViewInit {
  @ViewChild('panelBody') panelBodyRef?: ElementRef<HTMLElement>;
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);
  private readonly bottomBar = inject(DevBottomBarService);
  readonly monitor = inject(NetworkMonitorService);

  readonly activeTab = signal<Tab>(this.monitor.activeTab() as Tab || 'monitor');
  private readonly isMinimized = toSignal(this.bottomBar.minimized$, { requireSync: true });
  readonly consoleActive = toSignal(this.bottomBar.consoleVisible$, { requireSync: true });

  readonly methods = NETWORK_METHODS;
  readonly states = NETWORK_STATES;

  /** Whether the filter row is visible (collapsed by default). */
  readonly filtersVisible = signal(false);

  /** Dropdown open state for filter menus. */
  readonly methodDropdownOpen = signal(false);
  readonly stateDropdownOpen = signal(false);

  /** Path search query for filtering monitor entries. */
  readonly searchQuery = signal('');

  /** Summary label for the method dropdown button. */
  readonly methodFilterLabel = computed(() => {
    const enabled = this.monitor.enabledMethods();
    if (enabled.size === this.methods.length) return 'All Methods';
    if (enabled.size === 0) return 'No Methods';
    return [...enabled].join(', ');
  });

  /** Summary label for the state dropdown button. */
  readonly stateFilterLabel = computed(() => {
    const enabled = this.monitor.enabledStates();
    if (enabled.size === this.states.length) return 'All States';
    if (enabled.size === 0) return 'No States';
    return [...enabled].map(s => STATE_LABELS[s]).join(', ');
  });

  readonly allMethodsEnabled = computed(() => this.monitor.enabledMethods().size === this.methods.length);
  readonly allStatesEnabled = computed(() => this.monitor.enabledStates().size === this.states.length);

  /** True when any filter is narrowed (not all methods/states selected or search active). */
  readonly hasActiveFilters = computed(() =>
    !this.allMethodsEnabled() || !this.allStatesEnabled() || this.searchQuery().trim().length > 0
  );

  /** True when any filter is narrowed for the saved tab. */
  readonly hasActiveSavedFilters = computed(() =>
    !this.allMethodsEnabled() || !this.allStatesEnabled() || this.savedSearchQuery().trim().length > 0
  );

  /** All pinned entries from every source (unloaded history + loaded history + current session). */
  readonly allPinnedEntries = computed(() => {
    const fromUnloaded = this.monitor.pinnedFromHistory();
    const fromLoaded = this.monitor.loadedHistory().flatMap(s => s.entries.filter(e => e.pinned));
    const fromCurrent = this.monitor.currentSession().entries.filter(e => e.pinned);
    return [...fromUnloaded, ...fromLoaded, ...fromCurrent];
  });

  /** Count of active (enabled) mock rules. */
  readonly activeMockCount = computed(() => this.monitor.mocks().filter(m => m.enabled).length);

  /** Search query for filtering saved requests. */
  readonly savedSearchQuery = signal('');

  /** Folders that are collapsed (expanded by default). */
  readonly collapsedFolders = signal(new Set<string>());

  /** Favorites grouped by folder (name convention: "folder/name"). */
  readonly groupedFavorites = computed((): FolderGroup[] => {
    const query = this.savedSearchQuery().toLowerCase().trim();
    const enabledMethods = this.monitor.enabledMethods();
    const enabledStates = this.monitor.enabledStates();
    let favs = this.monitor.favorites();
    // Filter by method
    if (enabledMethods.size < this.methods.length) {
      favs = favs.filter(f => enabledMethods.has(f.method as any));
    }
    // Filter by state (derived from lastResponseStatus)
    if (enabledStates.size < this.states.length) {
      favs = favs.filter(f => {
        const state = this.deriveState(f.lastResponseStatus);
        return enabledStates.has(state);
      });
    }
    if (query) {
      favs = favs.filter(f =>
        f.name.toLowerCase().includes(query) || f.path.toLowerCase().includes(query)
      );
    }
    const groups = new Map<string, SavedRequest[]>();
    for (const f of favs) {
      const slashIdx = f.name.indexOf('/');
      const folder = slashIdx > 0 ? f.name.substring(0, slashIdx) : '';
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder)!.push(f);
    }
    // Ungrouped first, then folders alphabetically
    const result: FolderGroup[] = [];
    if (groups.has('')) result.push({ folder: '', items: groups.get('')! });
    for (const [folder, items] of [...groups.entries()].filter(([k]) => k).sort((a, b) => a[0].localeCompare(b[0]))) {
      result.push({ folder, items });
    }
    return result;
  });

  /** Folders that are collapsed in the mocks tab (expanded by default). */
  readonly collapsedMockFolders = signal(new Set<string>());

  /** Mocks grouped by folder (name convention: "folder/name"). */
  readonly groupedMocks = computed((): FolderGroup<MockRule>[] => {
    const mocks = this.monitor.mocks();
    const groups = new Map<string, MockRule[]>();
    for (const m of mocks) {
      const slashIdx = m.name.indexOf('/');
      const folder = slashIdx > 0 ? m.name.substring(0, slashIdx) : '';
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder)!.push(m);
    }
    const result: FolderGroup<MockRule>[] = [];
    if (groups.has('')) result.push({ folder: '', items: groups.get('')! });
    for (const [folder, items] of [...groups.entries()].filter(([k]) => k).sort((a, b) => a[0].localeCompare(b[0]))) {
      result.push({ folder, items });
    }
    return result;
  });

  /** Track whether user has manually scrolled up to disable auto-scroll. */
  private autoScrollEnabled = true;
  private lastEntryCount = 0;

  constructor() {
    // Non-live tabs have no place in the minimized HUD.
    effect(() => {
      if (this.isMinimized() && this.activeTab() !== 'monitor') {
        this.setTab('monitor');
      }
    });

    // Auto-scroll when new entries arrive
    effect(() => {
      const count = this.monitor.currentSession().entries.length;
      if (count > this.lastEntryCount && this.autoScrollEnabled) {
        this.lastEntryCount = count;
        setTimeout(() => this.scrollToBottom());
      } else {
        this.lastEntryCount = count;
      }
    });
  }

  ngAfterViewInit(): void {
    const el = this.panelBodyRef?.nativeElement;
    if (el) {
      el.addEventListener('scroll', () => {
        const threshold = 40;
        this.autoScrollEnabled = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - threshold);
      });
    }
  }

  private scrollToBottom(): void {
    const el = this.panelBodyRef?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  setTab(tab: Tab): void {
    this.activeTab.set(tab);
    this.monitor.setActiveTab(tab);
  }

  clearCurrent(): void {
    this.monitor.clearCurrent();
  }


  loadOneMoreSession(): void {
    this.monitor.loadOneMoreSession();
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
  }

  // ── Filter dropdowns ──────────────────────────────────────────────────────

  toggleMethodDropdown(event: Event): void {
    event.stopPropagation();
    this.stateDropdownOpen.set(false);
    this.methodDropdownOpen.update(v => !v);
  }

  toggleStateDropdown(event: Event): void {
    event.stopPropagation();
    this.methodDropdownOpen.set(false);
    this.stateDropdownOpen.update(v => !v);
  }

  closeDropdowns(): void {
    this.methodDropdownOpen.set(false);
    this.stateDropdownOpen.set(false);
  }

  toggleAllMethods(): void {
    this.monitor.setAllMethods(!this.allMethodsEnabled());
  }

  toggleAllStates(): void {
    this.monitor.setAllStates(!this.allStatesEnabled());
  }

  onDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeDropdowns();
  }

  // ── Filters ────────────────────────────────────────────────────────────────

  filterEntries(entries: NetworkEntry[]): NetworkEntry[] {
    const query = this.searchQuery().toLowerCase().trim();
    return entries.filter(e => {
      if (!this.monitor.passesFilters(e)) return false;
      if (query && !e.path.toLowerCase().includes(query)) return false;
      return true;
    });
  }

  /** Filter entries excluding pinned ones (they appear in the Pinned section). */
  filterUnpinnedEntries(entries: NetworkEntry[]): NetworkEntry[] {
    return this.filterEntries(entries.filter(e => !e.pinned));
  }

  isMethodEnabled(method: NetworkMethod): boolean {
    return this.monitor.enabledMethods().has(method);
  }

  isStateEnabled(state: NetworkEntryState): boolean {
    return this.monitor.enabledStates().has(state);
  }

  toggleMethod(method: NetworkMethod): void {
    this.monitor.toggleMethod(method);
  }

  toggleState(state: NetworkEntryState): void {
    this.monitor.toggleState(state);
  }

  stateLabel(state: NetworkEntryState): string {
    return STATE_LABELS[state];
  }

  /** Derive a state from a saved request's lastResponseStatus. */
  private deriveState(status?: number): NetworkEntryState {
    if (status === undefined) return 'pending';
    if (status === 0) return 'cancelled';
    if (status >= 200 && status < 400) return 'success';
    return 'error';
  }

  formatSessionDate(ts: number): string {
    const d = new Date(ts);
    return d.toLocaleString('es-ES', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  // ── Row event handlers ─────────────────────────────────────────────────────

  relaunch(entry: NetworkEntry): void {
    // Set entry back to pending state
    this.monitor.resetEntryForRetry(entry.id);
    this.http
      .request(entry.method, entry.url, { body: entry.requestBody, observe: 'response' })
      .subscribe({
        next: res => {
          this.monitor.completeRetry(entry.id, res.status, res.statusText ?? 'OK', res.body);
        },
        error: err => {
          const status = err?.status ?? 0;
          const text = err?.statusText ?? 'Error';
          const body = err?.error ?? String(err);
          this.monitor.completeRetry(entry.id, status, text, body);
        },
      });
  }

  relaunchSaved(fav: SavedRequest): void {
    const resolvedPath = this.monitor.resolveVariables(fav.path);
    const resolvedBody = this.monitor.resolveBodyVariables(fav.requestBody);
    const url = environment.baseApiUrl + (resolvedPath.startsWith('/') ? resolvedPath : '/' + resolvedPath);
    this.http.request(fav.method, url, { body: resolvedBody, observe: 'response' }).subscribe({
      next: res => {
        this.monitor.updateFavorite(fav.id, {
          lastResponse: res.body,
          lastResponseStatus: res.status,
          lastResponseAt: Date.now(),
        });
        if (fav.responseAlias) this.monitor.setVariable(fav.responseAlias, res.body);
      },
      error: err => {
        this.monitor.updateFavorite(fav.id, {
          lastResponse: err?.error ?? String(err),
          lastResponseStatus: err?.status ?? 0,
          lastResponseAt: Date.now(),
        });
      },
    });
  }

  saveFavorite(entry: NetworkEntry): void {
    this.dialog
      .open(SaveFavoriteDialog)
      .afterClosed()
      .subscribe((name: string | undefined) => {
        if (!name) return;
        this.monitor.addFavorite({
          name,
          method: entry.method,
          path: entry.path,
          requestBody: entry.requestBody,
        });
      });
  }

  /** Export current session as JSON (#10) */
  exportSession(): void {
    const session = this.monitor.currentSession();
    const history = this.monitor.loadedHistory();
    const data = {
      exportedAt: new Date().toISOString(),
      currentSession: {
        id: session.id,
        startedAt: session.startedAt,
        entries: session.entries,
      },
      historicSessions: history.map(s => ({
        id: s.id,
        startedAt: s.startedAt,
        entries: s.entries,
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-session-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  importSession(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      file.text().then(text => {
        try {
          const data = JSON.parse(text);
          this.monitor.importSession(data);
        } catch { /* invalid JSON */ }
      });
    };
    input.click();
  }

  copyToken(): void {
    const token = this.authService.getToken();
    if (token) navigator.clipboard.writeText(token);
  }

  togglePin(id: string): void {
    this.monitor.togglePin(id);
  }


  deleteCurrentEntry(id: string): void {
    this.monitor.remove(id);
  }

  deleteHistoricEntry(sessionId: string, entryId: string): void {
    this.monitor.removeHistoricEntry(sessionId, entryId);
  }

  deleteFavorite(id: string): void {
    this.monitor.removeFavorite(id);
  }

  duplicateFavorite(id: string): void {
    this.monitor.duplicateFavorite(id);
  }

  // ── Saved: folders ────────────────────────────────────────────────────────

  isFolderExpanded(folder: string): boolean {
    return !this.collapsedFolders().has(folder);
  }

  toggleFolder(folder: string): void {
    this.collapsedFolders.update(s => {
      const next = new Set(s);
      if (next.has(folder)) next.delete(folder); else next.add(folder);
      return next;
    });
  }

  runFolder(folder: string, event: Event): void {
    event.stopPropagation();
    const items = this.monitor.favorites().filter(f => {
      const slashIdx = f.name.indexOf('/');
      return slashIdx > 0 && f.name.substring(0, slashIdx) === folder;
    });
    items.forEach(fav => this.relaunchSaved(fav));
  }

  runFolderSequential(folder: string, event: Event): void {
    event.stopPropagation();
    const items = this.monitor.favorites().filter(f => {
      const slashIdx = f.name.indexOf('/');
      return slashIdx > 0 && f.name.substring(0, slashIdx) === folder;
    });
    this.runSequential(items, 0);
  }

  private runSequential(items: SavedRequest[], index: number): void {
    if (index >= items.length) return;
    const fav = items[index];
    const resolvedPath = this.monitor.resolveVariables(fav.path);
    const resolvedBody = this.monitor.resolveBodyVariables(fav.requestBody);
    const url = environment.baseApiUrl + (resolvedPath.startsWith('/') ? resolvedPath : '/' + resolvedPath);

    this.http.request(fav.method, url, { body: resolvedBody, observe: 'response' }).subscribe({
      next: res => {
        this.monitor.updateFavorite(fav.id, {
          lastResponse: res.body,
          lastResponseStatus: res.status,
          lastResponseAt: Date.now(),
        });
        if (fav.responseAlias) this.monitor.setVariable(fav.responseAlias, res.body);
        setTimeout(() => this.runSequential(items, index + 1), 500);
      },
      error: err => {
        this.monitor.updateFavorite(fav.id, {
          lastResponse: err?.error ?? String(err),
          lastResponseStatus: err?.status ?? 0,
          lastResponseAt: Date.now(),
        });
        setTimeout(() => this.runSequential(items, index + 1), 500);
      },
    });
  }

  // ── Saved: drag reorder ───────────────────────────────────────────────────

  onDragReorder(event: { fromId: string; toId: string }): void {
    const favs = [...this.monitor.favorites()];
    const fromIdx = favs.findIndex(f => f.id === event.fromId);
    const toIdx = favs.findIndex(f => f.id === event.toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = favs.splice(fromIdx, 1);
    favs.splice(toIdx, 0, moved);
    this.monitor.reorderFavorites(favs.map(f => f.id));
  }

  // ── Saved: import/export ──────────────────────────────────────────────────

  exportFavorites(): void {
    const data = this.monitor.exportFavorites();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'saved-requests.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  importFavorites(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      file.text().then(text => {
        try {
          const data = JSON.parse(text);
          if (Array.isArray(data)) {
            this.monitor.importFavorites(data);
          }
        } catch { /* invalid JSON */ }
      });
    };
    input.click();
  }

  // ── Mocks ──────────────────────────────────────────────────────────────────

  addMock(): void {
    this.monitor.addMock({
      name: 'New Mock',
      enabled: true,
      method: '',
      path: '',
      queryFilters: [],
      requestFilterFn: '',
      mockResponse: null,
      mockStatus: 200,
    });
  }

  deleteMock(id: string): void {
    this.monitor.removeMock(id);
  }

  duplicateMock(id: string): void {
    this.monitor.duplicateMock(id);
  }

  bulkToggleMocks(): void {
    const anyEnabled = this.monitor.mocks().some(m => m.enabled);
    this.monitor.bulkToggleMocks(!anyEnabled);
  }

  onMockDragReorder(event: { fromId: string; toId: string }): void {
    const mocks = [...this.monitor.mocks()];
    const fromIdx = mocks.findIndex(m => m.id === event.fromId);
    const toIdx = mocks.findIndex(m => m.id === event.toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = mocks.splice(fromIdx, 1);
    mocks.splice(toIdx, 0, moved);
    this.monitor.reorderMocks(mocks.map(m => m.id));
  }

  createMockFromEntry(entry: NetworkEntry): void {
    this.monitor.createMockFromEntry(entry);
  }

  // ── Mocks: folders ────────────────────────────────────────────────────────

  isMockFolderExpanded(folder: string): boolean {
    return !this.collapsedMockFolders().has(folder);
  }

  toggleMockFolder(folder: string): void {
    this.collapsedMockFolders.update(s => {
      const next = new Set(s);
      if (next.has(folder)) next.delete(folder); else next.add(folder);
      return next;
    });
  }

  toggleMockFolder_enable(folder: string, event: Event): void {
    event.stopPropagation();
    const mocks = this.monitor.mocks().filter(m => {
      const slashIdx = m.name.indexOf('/');
      return slashIdx > 0 && m.name.substring(0, slashIdx) === folder;
    });
    const anyEnabled = mocks.some(m => m.enabled);
    for (const m of mocks) {
      this.monitor.updateMock(m.id, { enabled: !anyEnabled });
    }
  }

  exportMocks(): void {
    const data = this.monitor.exportMocks();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mock-rules.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  importMocks(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      file.text().then(text => {
        try {
          const data = JSON.parse(text);
          if (Array.isArray(data)) this.monitor.importMocks(data);
        } catch { /* invalid JSON */ }
      });
    };
    input.click();
  }
}
